import { useQuery } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import { fetchDashboardData } from '@/api/dashboard.api'
import type { DashboardFilterParams } from '@/types/api'
import { DashboardFilterBar } from './components/DashboardFilterBar'
import { StatsCards } from './components/StatsCards'
import { SatisfactionChart } from './components/SatisfactionChart'
import { ServiceCountChart } from './components/ServiceCountChart'
import { ReviewStatsChart } from './components/ReviewStatsChart'
import { InteractionTimeChart } from './components/InteractionTimeChart'
import { StackedReviewChart } from './components/StackedReviewChart'
import { currentUserAtom } from '@/store/auth.atom'
import { useAtomValue } from 'jotai'
import { formatEndDate, formatStartDate } from '@/utils/format'
import { Button } from '@/components/ui/button'
import dayjs from 'dayjs'

export function DashboardPage() {
  const currentUser = useAtomValue(currentUserAtom)
  const showUnitColumn = currentUser?.userType === 1
  const [filterRequestKey, setFilterRequestKey] = useState(0)

  const [filter, setFilter] = useState<DashboardFilterParams>(() => {
    return {
      fromDate: formatStartDate(dayjs().startOf('month')) || undefined,
      toDate: formatEndDate(dayjs()) || undefined,
    }
  })

  useEffect(() => {
    if (currentUser) {
      setFilter((prev) => ({
        ...prev,
        unitId: currentUser.userType !== 1 ? currentUser.unitId : undefined,
      }))
    }
  }, [currentUser])

  const handleFilterChange = (nextFilter: DashboardFilterParams) => {
    setFilter(nextFilter)
    setFilterRequestKey((prev) => prev + 1)
  }

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['dashboard', filter, filterRequestKey],
    queryFn: ({ signal }) => fetchDashboardData(filter, signal),
    enabled: !!currentUser,
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })

  if (!currentUser || isLoading) {
    return (
      <section className="-mx-4 md:-mx-8 -mb-12 min-h-[calc(100vh-64px)] px-4 md:px-8 pb-12 pt-6">
        <div className="py-12 text-center text-gray-500">
          Đang tải dữ liệu...
        </div>
      </section>
    )
  }

  if (isError || !data) {
    return (
      <section className="-mx-4 md:-mx-8 -mb-12 min-h-[calc(100vh-64px)] px-4 md:px-8 pb-12 pt-6">
        <div className="mx-auto flex max-w-lg flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-red-300 bg-red-50 px-6 py-10 text-center">
          <h2 className="text-base font-semibold text-red-700">
            Không thể tải dữ liệu
          </h2>
          <Button
            variant="primary"
            size="md"
            onClick={() => refetch()}
            disabled={isFetching}
            className="h-10 bg-red-600 hover:bg-red-700 text-white"
          >
            {isFetching ? 'Đang thử lại...' : 'Thử lại'}
          </Button>
        </div>
      </section>
    )
  }

  return (
    <section className="select-none -mx-4 md:-mx-8 -mb-12 min-h-[calc(100vh-64px)] space-y-3 px-4 md:px-8 pb-12 pt-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between">
        <DashboardFilterBar
          value={filter}
          onChange={handleFilterChange}
          showUnitColumn={showUnitColumn}
        />
      </div>

      <StatsCards stats={data.stats} />

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3 lg:items-stretch">
        <div className="min-h-[340px]">
          <ServiceCountChart data={data.receptionRateByRobots} />
        </div>
        <div className="min-h-[340px]">
          <InteractionTimeChart
            data={(data as any).receptionDurationByRobots}
          />
        </div>
        <div className="min-h-[340px]">
          <StackedReviewChart data={data.surveyStatsByTarget} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:items-stretch">
        <div className="min-h-[340px] lg:col-span-1">
          <SatisfactionChart
            weekData={(data as any).satisfactionWeek}
            monthData={(data as any).satisfactionMonth}
          />
        </div>
        <div className="min-h-[340px] lg:col-span-1">
          <ReviewStatsChart data={data.problemNoteStats} />
        </div>
      </div>
    </section>
  )
}
